/**
 * useDossierExport — React hook that wraps the dossier ZIP generator
 * and exposes reactive progress + a trigger function.
 */
import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  generateDossierZip,
  type DossierProgress,
} from "@/lib/dossierExport";

const INITIAL: DossierProgress = {
  totalFiles: 0,
  completedFiles: 0,
  currentFile: null,
  files: [],
  phase: "preparing",
};

export function useDossierExport() {
  const [progress, setProgress] = useState<DossierProgress>(INITIAL);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isMounted = useRef(true);
  const queryClient = useQueryClient();

  const start = useCallback(async (embarqueId: string, numeroDo: string) => {
    if (busy) return;
    setBusy(true);
    setOpen(true);
    setProgress({ ...INITIAL });
    try {
      await generateDossierZip(embarqueId, numeroDo, (p) => {
        if (isMounted.current) setProgress({ ...p });
      });
      // Refresh signature badge / audit card after sealing
      queryClient.invalidateQueries({ queryKey: ["dossier-signature", embarqueId] });
      toast.success("Dossier DIAN generado", {
        description: `Descargando ZIP de ${numeroDo}`,
      });
    } catch (err: any) {
      setProgress((prev) => ({ ...prev, phase: "error", message: err?.message }));
      toast.error("Error al generar el dossier", { description: err?.message });
    } finally {
      setBusy(false);
    }
  }, [busy, queryClient]);

  const close = useCallback(() => {
    if (busy) return; // prevent closing mid-download
    setOpen(false);
  }, [busy]);

  return { progress, open, busy, start, close };
}
